import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector, effect, inject, untracked } from '@angular/core';
import { BrowserWindow } from '@core/misc/browser-window.service';
import { dispatch, select } from '@ngxs/store';
import { ConfirmService } from '../../../shared/modules/confirm/confirm.service';
import {
  authAdoptRefreshToken,
  authLogOut,
  authSessionActivity,
  authVerifyAccessToken
} from '../auth/state/auth.actions';
import { AuthState } from '../auth/state/auth.state';
import { AUTH_REFRESH_TOKEN_STORAGE_KEY, AUTH_SESSION_EXPIRED_STORAGE_KEY } from '../auth/state/auth.const';
import { SessionCheckComponent } from './session-check.component';
import {
  SESSION_CHECK_ACTIVITY_EVENTS,
  SESSION_CHECK_ACTIVITY_MESSAGE,
  SESSION_CHECK_ACTIVITY_THROTTLE_MS,
  SESSION_CHECK_BROADCAST_CHANNEL,
  SESSION_CHECK_LOGGED_IN_MESSAGE,
  SESSION_CHECK_LOGGED_OUT_MESSAGE,
  SESSION_CHECK_WARNING_MS,
  sessionCheckDeadlineAt
} from './session-check.const';

/**
 * Logs the user out after `environment.sessionTimer` minutes without user activity.
 *
 * Activity is tracked through DOM events (typing, clicking, scrolling) and stamped
 * into `AuthState.lastActivityAt`. Records are shared across tabs through a
 * BroadcastChannel, so working in any tab of the app keeps every tab's session
 * alive. One minute before the inactivity deadline a warning modal
 * (`SessionCheckComponent`) opens; the modal counts down against the wall clock
 * and logs out when the deadline passes. While the modal is open only its
 * "Refresh Session" button (or activity in another tab) can extend the session.
 */
@Injectable()
export class SessionCheckService {
  readonly #injector = inject(Injector);
  readonly #confirmService = inject(ConfirmService);
  readonly #window = inject(BrowserWindow);

  readonly #isAuthenticated = select(AuthState.isAuthenticated);
  readonly #refreshTokenLoadStatus = select(AuthState.refreshTokenLoadStatus);
  readonly #lastActivityAt = select(AuthState.lastActivityAt);
  readonly #sessionExpiredAt = select(AuthState.sessionExpiredAt);

  readonly #sessionActivity = dispatch(authSessionActivity);
  readonly #verifyAccessToken = dispatch(authVerifyAccessToken);
  readonly #logOut = dispatch(authLogOut);
  readonly #adoptRefreshToken = dispatch(authAdoptRefreshToken);

  #isModalOpen = false;
  #evaluateTimer: ReturnType<typeof setTimeout> | null = null;
  #broadcastChannel: BroadcastChannel | null = null;

  public init(): void {
    this.#runActivityListeners();
    this.#runVisibilityListener();
    this.#runCrossTabListener();
    this.#runAuthListener();
    this.#runSessionListener();
  }

  #runActivityListeners(): void {
    for (const eventType of SESSION_CHECK_ACTIVITY_EVENTS) {
      document.addEventListener(eventType, this.#onUserActivity, { capture: true, passive: true });
    }
  }

  /** Re-checks the session as soon as the tab becomes visible — background tabs throttle timers. */
  #runVisibilityListener(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      this.#verifyAccessToken();
      this.#evaluate();
    });
  }

  #runCrossTabListener(): void {
    this.#broadcastChannel = new BroadcastChannel(SESSION_CHECK_BROADCAST_CHANNEL);
    this.#broadcastChannel.onmessage = ({ data }: MessageEvent) => {
      if (data === SESSION_CHECK_LOGGED_IN_MESSAGE) this.#onRemoteLogIn();
      else if (data === SESSION_CHECK_LOGGED_OUT_MESSAGE) this.#onRemoteLogOut();
      else this.#onRemoteActivity();
    };

    this.#runStorageListener();
  }

  /**
   * Tabs share the session (docs#205, R-SAL29): the refresh token in localStorage is the session. When another tab clears it - a Log Out,
   * or an expiry - this tab ends too, also where BroadcastChannel is missing or a message was lost. `storage` events fire in the OTHER tabs
   * only, so a tab never reacts to its own log-out. A NEW value (a renewal, a sign-in) is not a sign-out.
   */
  #runStorageListener(): void {
    this.#window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key !== AUTH_REFRESH_TOKEN_STORAGE_KEY) return;
      const renewed = this.#storedJson(event.newValue);
      if (renewed) {
        // a renewal or sign-in in another tab: follow its token, or this tab's stale copy would be written back over it.
        // Follow what is stored NOW: an event can arrive after a newer token replaced its value (a reloading tab still receives the ones
        // queued before its load), and adopting that older token would store one the server already deleted (website-web#59).
        const current = this.#storedJson(this.#window.localStorage?.getItem(AUTH_REFRESH_TOKEN_STORAGE_KEY) ?? null);
        const newest = typeof current === 'string' && current ? current : renewed;
        if (typeof newest === 'string' && this.#isAuthenticated()) this.#adoptRefreshToken(newest);
        return;
      }

      // The storage plugin writes the token and the expiry flag in one task, and a reloading tab can write the empty default before it
      // hydrates: decide only after that task, from what the storage holds NOW - a token that is back means nobody signed out.
      setTimeout(() => {
        if (!this.#isAuthenticated()) return;
        if (this.#storedJson(this.#window.localStorage?.getItem(AUTH_REFRESH_TOKEN_STORAGE_KEY) ?? null)) return;
        const expired = Boolean(
          this.#storedJson(this.#window.localStorage?.getItem(AUTH_SESSION_EXPIRED_STORAGE_KEY) ?? null)
        );
        this.#logOut(expired ? true : undefined);
      }, 0);
    });
  }

  /** A value the storage plugin wrote (JSON): truthy when it holds something (a token, a date), falsy for null, "" or garbage. */
  #storedJson(raw: string | null): unknown {
    try {
      return raw === null ? null : JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /**
   * Counts logging in (and every page load of an authenticated session) as activity
   * and announces session transitions to other tabs. An explicit log-out is
   * broadcast too; an expired session is not — every tab reaches the shared
   * deadline on its own and keeps its "session expired" notice that way.
   */
  #runAuthListener(): void {
    let wasAuthenticated = false;
    effect(
      () => {
        const isAuthenticated = this.#isAuthenticated();
        untracked(() => {
          if (isAuthenticated) {
            this.#recordActivity();
            this.#broadcastChannel?.postMessage(SESSION_CHECK_LOGGED_IN_MESSAGE);
          } else if (wasAuthenticated && !this.#sessionExpiredAt()) {
            this.#broadcastChannel?.postMessage(SESSION_CHECK_LOGGED_OUT_MESSAGE);
          }
          wasAuthenticated = isAuthenticated;
        });
      },
      { injector: this.#injector }
    );
  }

  /**
   * Applies activity reported by other tabs to this tab's state. The record is
   * intentionally not re-broadcast, so tabs cannot ping-pong each other.
   */
  #onRemoteActivity(): void {
    if (!this.#isAuthenticated()) return;
    if (Date.now() - (this.#lastActivityAt() ?? 0) < SESSION_CHECK_ACTIVITY_THROTTLE_MS) return;
    this.#sessionActivity();
  }

  /**
   * A signed-out tab (parked on the log-in page) follows a log-in from another tab
   * by reloading: the session hydrates from storage and the auth guard redirects
   * away from the log-in page to the default one.
   */
  #onRemoteLogIn(): void {
    if (this.#isAuthenticated()) return;
    // website-web#59 (R-WLT7): a tab whose renewal is in flight is already on its way in - a reload would cancel the request after the
    // server had rotated the token, and the tab would come back with one the server already deleted
    if (this.#refreshTokenLoadStatus()?.status === 'loading') return;
    this.#window.location.reload();
  }

  /** Follows an explicit log-out from another tab by ending this tab's session too. */
  #onRemoteLogOut(): void {
    if (!this.#isAuthenticated()) return;
    this.#logOut();
  }

  /** Every activity record re-schedules the warning; log-out (activity cleared) cancels it. */
  #runSessionListener(): void {
    effect(
      () => {
        const lastActivityAt = this.#lastActivityAt();
        untracked(() => (lastActivityAt ? this.#evaluate() : this.#clearEvaluateTimer()));
      },
      { injector: this.#injector }
    );
  }

  readonly #onUserActivity = (): void => {
    if (this.#isModalOpen) return;
    if (Date.now() - (this.#lastActivityAt() ?? 0) < SESSION_CHECK_ACTIVITY_THROTTLE_MS) return;
    this.#recordActivity();
  };

  #recordActivity(): void {
    if (!this.#isAuthenticated()) return;
    this.#sessionActivity();
    this.#broadcastChannel?.postMessage(SESSION_CHECK_ACTIVITY_MESSAGE);
  }

  /** Schedules the warning modal for the current deadline, or opens it when the warning window has begun. */
  #evaluate(): void {
    this.#clearEvaluateTimer();
    if (this.#isModalOpen) return;

    const lastActivityAt = this.#lastActivityAt();
    if (!lastActivityAt) return;

    const warningInMs = sessionCheckDeadlineAt(lastActivityAt) - SESSION_CHECK_WARNING_MS - Date.now();
    if (warningInMs <= 0) this.#openSessionEndingModal();
    else this.#evaluateTimer = setTimeout(() => this.#evaluate(), warningInMs);
  }

  #clearEvaluateTimer(): void {
    if (this.#evaluateTimer === null) return;
    clearTimeout(this.#evaluateTimer);
    this.#evaluateTimer = null;
  }

  #openSessionEndingModal(): void {
    this.#isModalOpen = true;
    this.#confirmService
      .confirm<SessionCheckComponent>(
        {
          title: 'Session Ending Soon',
          componentPortal: new ComponentPortal(SessionCheckComponent)
        },
        {
          icon: 'timer',
          backDropClose: true
        },
        {
          footerStyleClass: 'confirmation-footer-center-actions',
          primaryButton: {
            label: 'Refresh Session',
            handler: () => {
              this.#verifyAccessToken(true);
            }
          }
        }
      )
      .subscribe(() => {
        this.#isModalOpen = false;
        this.#recordActivity();
      });
  }
}
